import { AiCapturedBadge } from "@prototypes/ai-capture/AiCapturedBadge";
import classNames from "classnames";
import React, {
    type Ref,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from "react";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import Label from "./Label";
import "./Input.css";
import "./Input.Heading.css";
import { Email } from "@albertine/types";
import { isEmpty } from "../../common/utils/string.util";
import Button from "./Buttons";
import { Flex, FlexInline } from "./Flex";
import Icon from "./Icon";

type CommonInputProps = {
    className?: string; // if set will override the default styling

    value: string;
    placeholder?: string;
    label?: string;
    supportingText?: string;

    disabled?: boolean;
    highlight?: boolean;
    readOnly?: boolean;
    optionToClear?: boolean;

    trim?: boolean;
    lowercase?: boolean;
    autoFocus?: boolean;
    maxLength?: number;

    isValid?: (value: string) => boolean;

    onChange?: (value: string) => Promise<any> | void; // triggers with every keystroke
    onChangeIsValid?: (value: string) => Promise<any> | void; // triggers with every keystroke if the input is valid

    onBlur?: (value: string) => Promise<any> | void; // triggers when the input loses focus
    onBlurIsValid?: (value: string) => Promise<any> | void; // triggers when the input loses focus if the input is valid

    onFocus?: (value: string) => void;
};

type InputProps = CommonInputProps & {
    prefix?: string;
    autoComplete?: "on" | "off" | undefined;
    onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
} & Pick<React.InputHTMLAttributes<HTMLInputElement>, "type">;

type CheckboxProps = {
    className?: string; // if set will override the default styling

    checked: boolean;
    placeholder?: string;
    label: string;
    supportingText?: string;

    disabled?: boolean;
    highlight?: boolean;
    readOnly?: boolean;

    trim?: boolean;
    lowercase?: boolean;
    autoFocus?: boolean;
    maxLength?: number;
    isValid?: (value: boolean) => boolean;

    onChange?: (value: boolean) => Promise<any> | void; // triggers with every keystroke
    onChangeIsValid?: (value: boolean) => Promise<any> | void; // triggers with every keystroke if the input is valid
};

type TextAreaProps = CommonInputProps & {
    rows?: number;
    autoResize?: boolean;
    removeLineBreaks?: boolean;
} & Pick<React.InputHTMLAttributes<HTMLInputElement>, "type">;

function replaceNewLinesWithSpaces(value: string) {
    return value.replace(/\r?\n|\r/g, " ");
}

function replaceMultilineSpaces(value: string) {
    return value.replace(/\s+/g, " ");
}

function formatValue(
    input: string,
    props: InputProps & TextAreaProps,
    fn: "onChange" | "onBlur",
): string {
    const { removeLineBreaks, trim, lowercase } = props;
    let output = input;
    if (removeLineBreaks) output = replaceNewLinesWithSpaces(output);
    if (trim) {
        output = fn === "onChange" ? output.trimStart() : output.trim();
        output = replaceMultilineSpaces(output);
    }
    if (lowercase) output = output.toLowerCase();
    return output;
}

export type InputActions = {
    reset: () => void;
    focus: () => void;
};

function BaseInput(type: React.HTMLInputTypeAttribute) {
    return React.forwardRef((props: InputProps, ref: Ref<InputActions>) => {
        const {
            className,
            value,
            label,
            prefix,
            placeholder,
            supportingText,
            disabled,
            readOnly,
            highlight,
            autoFocus,
            autoComplete,
            maxLength,
            optionToClear,
            onChange,
            onChangeIsValid,
            onBlur,
            onBlurIsValid,
            onFocus,
            onKeyDown,
            isValid,
        } = props;

        const [currentValue, setCurrentValue] = useState(value);
        const [isEdited, setIsEdited] = useState(false);
        const [isFocused, setIsFocused] = useState(false);
        const [isSaving, setIsSaving] = useState(false);

        const inputRef = useRef<HTMLInputElement | null>(null);
        useImperativeHandle(ref, () => ({
            reset: () => {
                setCurrentValue("");
            },
            focus: () => inputRef.current?.focus(),
        }));

        const triggerOnChange = async (newValue: string) => {
            try {
                if (onChange) await onChange(newValue);
                if (onChangeIsValid && isValid?.(newValue))
                    await onChangeIsValid(newValue);
            } catch (error) {
                setCurrentValue(value || "");
            }
        };

        const triggerOnBlur = async (newValue: string) => {
            try {
                setIsSaving(true);
                if (isEdited && onBlur) await onBlur(newValue);
                if (isEdited && onBlurIsValid && isValid?.(newValue))
                    await onBlurIsValid(newValue);
            } catch (error) {
                setCurrentValue(value || "");
            } finally {
                setIsSaving(false);
            }
        };

        return (
            <div
                className={classNames(className || "input__container", {
                    "input--disabled": disabled,
                    "input--is-saving": isSaving,
                    "input--invalid": isValid && !isValid(currentValue),
                    "input--edited": isEdited,
                    "input--readonly": readOnly,
                    "input--focused": isFocused,
                    "input--with-clear": optionToClear,
                })}
                onClick={() => {
                    inputRef.current?.focus();
                }}
            >
                <div
                    className={classNames(
                        "input",
                        highlight && "!border-blue-200 !bg-blue-50 !pr-7",
                    )}
                >
                    {label && <label className="input__label">{label}</label>}
                    <FlexInline gap="medium">
                        {prefix && <span className="input__prefix">{prefix}</span>}
                        <input
                            ref={inputRef}
                            type={type}
                            className={classNames(
                                type === "number" && "number_input",
                                highlight && "!bg-transparent",
                            )}
                            placeholder={placeholder}
                            value={currentValue}
                            autoFocus={autoFocus}
                            autoComplete={autoComplete || "off"}
                            readOnly={readOnly}
                            disabled={disabled}
                            maxLength={maxLength}
                            onWheel={(e) => {
                                e.currentTarget.blur();
                            }}
                            onFocus={() => {
                                if (!readOnly) setIsFocused(true);
                                if (onFocus) onFocus(value);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") e.currentTarget.blur();
                            }}
                            onChange={async (e) => {
                                setIsEdited(true);
                                const newValue = formatValue(
                                    e.target.value,
                                    props,
                                    "onChange",
                                );
                                setCurrentValue(newValue);
                                await triggerOnChange(newValue);
                            }}
                            onBlur={async () => {
                                setIsFocused(false);
                                const newValue = formatValue(
                                    currentValue,
                                    props,
                                    "onBlur",
                                );
                                if (newValue !== currentValue) {
                                    setCurrentValue(newValue);
                                    await triggerOnChange(newValue);
                                }
                                await triggerOnBlur(newValue);
                                setIsEdited(false);
                            }}
                            onKeyDownCapture={
                                onKeyDown
                                    ? (event) => {
                                          onKeyDown(event);
                                      }
                                    : undefined
                            }
                        />
                    </FlexInline>
                    {isSaving && (
                        <div className="input__loading-spinner">
                            <LoadingSpinner />
                        </div>
                    )}
                    {!isEmpty(currentValue) && optionToClear && (
                        <Button.Ghost
                            className="input__clear-button"
                            onClick={async () => {
                                setIsEdited(true);
                                const newValue = "";
                                setCurrentValue(newValue);
                                await triggerOnChange(newValue);
                            }}
                        >
                            <Icon.Close.Small />
                        </Button.Ghost>
                    )}
                    {highlight && <AiCapturedBadge />}
                </div>
                {supportingText && (
                    <Label.Small.N60 className="input__support-text">
                        {supportingText}
                    </Label.Small.N60>
                )}
            </div>
        );
    });
}

const TextArea = React.forwardRef(
    (props: TextAreaProps, ref: Ref<HTMLTextAreaElement | null>) => {
        const {
            className,
            value,
            label,
            placeholder,
            supportingText,
            disabled,
            readOnly,
            highlight,
            autoFocus,
            rows,
            autoResize,
            removeLineBreaks,
            maxLength,
            onChange,
            onChangeIsValid,
            onBlur,
            onBlurIsValid,
            isValid,
        } = props;

        const textAreaRef = useRef<HTMLTextAreaElement>(null);
        useImperativeHandle(ref, () => {
            if (!textAreaRef.current) {
                throw new Error(
                    "TextArea ref is null - component not properly mounted",
                );
            }
            return textAreaRef.current;
        });

        const [currentValue, setCurrentValue] = useState(value || "");
        const [isEdited, setIsEdited] = useState(false);
        const [isFocused, setIsFocused] = useState(false);
        const [isSaving, setIsSaving] = useState(false);

        function applyAutoResize() {
            if (textAreaRef.current) {
                textAreaRef.current.style.height = "auto";
                textAreaRef.current.style.height = `${textAreaRef.current.scrollHeight}px`;
            }
        }

        useEffect(() => {
            if (autoResize) applyAutoResize();
        }, [autoResize, textAreaRef, currentValue]);

        const triggerOnChange = async (newValue: string) => {
            try {
                if (onChange) await onChange(newValue);
                if (onChangeIsValid && isValid?.(newValue))
                    await onChangeIsValid(newValue);
            } catch (error) {
                setCurrentValue(value || "");
            }
        };

        const triggerOnBlur = async (newValue: string) => {
            try {
                setIsSaving(true);
                if (isEdited && onBlur) await onBlur(newValue);
                if (isEdited && onBlurIsValid && isValid?.(newValue))
                    await onBlurIsValid(newValue);
            } catch (error) {
                setCurrentValue(value || "");
            } finally {
                setIsSaving(false);
            }
        };

        return (
            <div
                className={classNames(className || "textarea__container", {
                    "textarea--disabled": disabled,
                    "textarea--is-saving": isSaving,
                    "textarea--invalid": isValid && !isValid(currentValue),
                    "textarea--edited": isEdited,
                    "textarea--readonly": readOnly,
                    "textarea--focused": isFocused,
                    "textarea--autoResize": autoResize,
                })}
                onClick={() => {
                    textAreaRef.current?.focus();
                }}
            >
                <div
                    className={classNames(
                        "textarea",
                        highlight && "!border-blue-200 !bg-blue-50 !pr-6",
                    )}
                >
                    {label && <label className="textarea__label">{label}</label>}
                    <textarea
                        ref={textAreaRef}
                        placeholder={placeholder}
                        value={currentValue}
                        autoFocus={autoFocus}
                        autoComplete="off"
                        rows={rows || 3}
                        readOnly={readOnly}
                        disabled={disabled}
                        maxLength={maxLength}
                        className={highlight ? "!bg-transparent" : undefined}
                        onFocus={() => {
                            if (!readOnly) setIsFocused(true);
                        }}
                        onKeyDown={(e) => {
                            if (removeLineBreaks && e.key === "Enter") {
                                e.currentTarget.blur();
                            }
                            if ((e.ctrlKey || e.metaKey) && e.key === "Enter")
                                e.currentTarget.blur();
                        }}
                        onChange={async (e) => {
                            setIsEdited(true);
                            const newValue = formatValue(
                                e.target.value,
                                props,
                                "onChange",
                            );
                            setCurrentValue(newValue);
                            await triggerOnChange(newValue);
                        }}
                        onBlur={async () => {
                            setIsFocused(false);
                            const newValue = formatValue(currentValue, props, "onBlur");
                            if (newValue !== currentValue) {
                                setCurrentValue(newValue);
                                await triggerOnChange(newValue);
                            }
                            await triggerOnBlur(newValue);
                            setIsEdited(false);
                        }}
                    />
                    {isSaving && (
                        <div className="textarea__loading-spinner">
                            <LoadingSpinner />
                        </div>
                    )}
                    {highlight && <AiCapturedBadge />}
                </div>
                {supportingText && (
                    <Label.Small.N60 className="textarea__support-text">
                        {supportingText}
                    </Label.Small.N60>
                )}
            </div>
        );
    },
);

const Checkbox = React.forwardRef(
    (props: CheckboxProps, ref: Ref<HTMLInputElement | null>) => {
        const {
            className,
            checked,
            label,
            placeholder,
            supportingText,
            disabled,
            readOnly,
            highlight,
            autoFocus,
            maxLength,
            onChange,
            isValid,
        } = props;

        const inputRef = useRef<HTMLInputElement>(null);
        useImperativeHandle(ref, () => {
            if (!inputRef.current) {
                throw new Error(
                    "Checkbox ref is null - component not properly mounted",
                );
            }
            return inputRef.current;
        });

        const [currentValue, setCurrentValue] = useState<boolean>(checked);
        const [isEdited, setIsEdited] = useState<boolean>(false);
        const [isFocused, setIsFocused] = useState<boolean>(false);

        const triggerOnChange = async () => {
            if (!readOnly) {
                setIsEdited(true);
                const newValue = !currentValue;
                setCurrentValue(newValue);

                try {
                    if (onChange) await onChange(newValue);
                } catch (error) {
                    setCurrentValue(currentValue || false);
                }
            }
        };

        return (
            <div
                className={classNames(className || "checkbox__container", {
                    "checkbox--disabled": disabled,
                    "checkbox--invalid": isValid && !isValid(currentValue),
                    "checkbox--edited": isEdited,
                    "checkbox--readonly": readOnly,
                    "checkbox--focused": isFocused,
                })}
                onClick={() => {
                    inputRef.current?.focus();
                }}
            >
                <div className="checkbox">
                    <label className="checkbox__label">
                        <Flex.Row gap="medium" alignItems="start">
                            <Flex.Column
                                alignItems="center"
                                justifyContent="center"
                                className={classNames("checkbox__checkmark", {
                                    "checkbox__checkmark--checked": currentValue,
                                    "!border-blue-200 !bg-blue-50": highlight,
                                    "!border-blue-500 !bg-blue-500":
                                        highlight && currentValue,
                                })}
                            >
                                {currentValue && <Icon.Check.Small.N100 />}
                            </Flex.Column>
                            <input
                                ref={inputRef}
                                type="checkbox"
                                placeholder={placeholder}
                                checked={currentValue}
                                autoFocus={autoFocus}
                                disabled={disabled}
                                maxLength={maxLength}
                                onFocus={() => {
                                    if (!readOnly) setIsFocused(true);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") e.currentTarget.blur();
                                }}
                                onChange={triggerOnChange}
                            />
                            <Flex.Column gap="small">
                                <Label.Medium.Light.N10>{label}</Label.Medium.Light.N10>
                                {supportingText && (
                                    <Label.Small.N60 className="input__support-text">
                                        {supportingText}
                                    </Label.Small.N60>
                                )}
                            </Flex.Column>
                        </Flex.Row>
                    </label>
                </div>
            </div>
        );
    },
);

// This pattern is to quarantine that the component rerenders when the string value changes
function forceRerenderOnValueChanges<Props extends InputProps | TextAreaProps>(
    WrappedComponent: React.ComponentType<Props>,
): React.ComponentType<Props> {
    return function WrapInputToRerenderWithKey(props: Props) {
        const { value } = props;
        return (
            <React.Fragment key={value}>
                <WrappedComponent {...props} />
            </React.Fragment>
        );
    };
}

// This pattern is to quarantine that the component rerenders when the boolean value changes
function forceRerenderOnBooleanValueChanges<Props extends CheckboxProps>(
    WrappedComponent: React.ComponentType<Props>,
): React.ComponentType<Props> {
    return function WrapInputToRerenderWithKey(props: Props) {
        const { checked } = props;
        return (
            <React.Fragment key={checked ? "on" : "off"}>
                <WrappedComponent {...props} />
            </React.Fragment>
        );
    };
}

// Set the default parameters for the Email input component
function EmailDefaults<Props extends InputProps>(
    InputComponent: React.ComponentType<Props>,
): React.ComponentType<Props> {
    return function InputEmailWrapper(props: Props) {
        return (
            <InputComponent
                isValid={(value) => Email.safeParse(value).success}
                placeholder="example@example.com"
                lowercase
                trim
                {...props}
            />
        );
    };
}

const Input = {
    Checkbox: forceRerenderOnBooleanValueChanges(Checkbox),
    TextField: forceRerenderOnValueChanges(BaseInput("textfield")),
    PersistentTextField: BaseInput("textfield"),
    Email: forceRerenderOnValueChanges(EmailDefaults(BaseInput("email"))),
    TextArea: forceRerenderOnValueChanges(TextArea),
    Number: forceRerenderOnValueChanges(BaseInput("number")),
    PersistentNumber: BaseInput("number"),
};

export default Input;
